import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { HierarchyTree } from './hierarchy/HierarchyTree'
import type { HierarchyMove, HierarchyTreeNode } from './hierarchy/HierarchyTree'
import type { components } from '../api/schema'

type DocumentType = components['schemas']['DocumentType']

interface DocumentTypesTreeProps {
  documentTypes: DocumentType[]
  selectedUid?: string
  onSelect: (uid: string) => void
  /** Enables the edit-mode toggle. Document types re-parent only — never reorder. */
  editable?: boolean
  editing?: boolean
  onEditingChange?: (editing: boolean) => void
  onMove?: (move: HierarchyMove) => void
  /** Overrides the "nothing here" label — e.g. a quick filter with no match. */
  emptyLabel?: string
}

export function documentTypeLabel(dt: DocumentType): string {
  if (typeof dt.name === 'object' && dt.name !== null) {
    return dt.name.en || dt.name.fr || Object.values(dt.name)[0] || dt.code
  }
  return dt.name || dt.code
}

/**
 * Document-type hierarchy browser. A thin mapping onto the shared
 * {@link HierarchyTree}: no `ordering` is persisted for document types, so the
 * tree renders alphabetically and drag & drop (when enabled) only re-parents.
 */
export function DocumentTypesTree({
  documentTypes,
  selectedUid,
  onSelect,
  editable = false,
  editing = false,
  onEditingChange,
  onMove,
  emptyLabel,
}: DocumentTypesTreeProps) {
  const { t } = useTranslation('types')

  const nodes: HierarchyTreeNode[] = useMemo(
    () =>
      documentTypes.map(dt => ({
        uid: dt.uid,
        label: documentTypeLabel(dt),
        parentUid: dt.parent_document_type_uid || null,
      })),
    [documentTypes]
  )

  return (
    <HierarchyTree
      nodes={nodes}
      selectedUid={selectedUid}
      onSelect={onSelect}
      editable={editable}
      editing={editing}
      onEditingChange={onEditingChange}
      onMove={onMove}
      testIdPrefix="document-types"
      labels={{
        edit: t('document_types.tree.edit_mode'),
        done: t('document_types.tree.edit_mode_done'),
        root: t('document_types.tree.root'),
        dropToRoot: t('document_types.tree.drop_to_root'),
        empty: emptyLabel ?? t('document_types.tree.empty'),
        addChild: t('document_types.tree.add_child'),
      }}
    />
  )
}
